import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, FileTextIcon, LayersIcon, LayoutGridIcon, MapIcon, WavesIcon, UsersIcon, TimerIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { PAGE_IMAGE_1, PAGE_IMAGE_2, PAGE_IMAGE_3, PAGE_IMAGE_4, PAGE_IMAGE_5 } from './assets';

// All landing copy for IDRÆT KLAR™ (Danish, folkeskolen 0.–9. klasse).
// data/liikunta.ts and data/matikka.ts only re-export this module.
export const BRAND = 'IDRÆT KLAR™';
export const heroGuarantees = ['Øjeblikkelig digital adgang', 'Engangsbetaling', 'Intet abonnement', '7 dages tilfredshedsgaranti'];
export const stats = [{
  value: '6',
  label: 'Digitale bind'
}, {
  value: '240',
  label: 'Sider i alt'
}, {
  value: '5',
  label: 'Bonusser med i pakken'
}, {
  value: 'PDF',
  label: 'Klar til print'
}, {
  value: '0.–9.',
  label: 'Klasse – fra indskoling til udskoling'
}];
export const differenceStats = [{
  value: '0.–9. klasse',
  label: 'Fra indskoling til udskoling'
}, {
  value: '6 digitale bind',
  label: 'Fra leg og kropsbasis til boldspil, udeliv, svømning, træning og evaluering'
}, {
  value: 'Printklare lektionskort',
  label: 'Brug dem på tablet, eller print dem og tag dem med i idrætshallen'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Aftener og weekender forsvinder',
  text: 'Du planlægger næste uges idrætstimer foran skærmen – og alligevel starter timen tit med improvisation.'
}, {
  title: 'Hver lektion planlægges fra bunden',
  text: 'Opvarmning, hoveddel, afslutning, materialer og holdinddeling – til hver eneste lektion.'
}, {
  title: '25 elever på vidt forskellige niveauer',
  text: 'Nogle dyrker sport hver dag i fritiden, andre er nervøse bare for at gribe en bold – og timen skal fungere for alle.'
}, {
  title: 'Hallkort og opstilling i hovedet',
  text: 'Hvor skal stationerne stå, hvor skal keglerne stå, og hvordan roterer holdene? Alt skal være tænkt igennem, før eleverne kommer ind i hallen.'
}, {
  title: 'Evaluering og standpunkt er svære at begrunde',
  text: 'Tegn på læring, observation, selvevaluering og standpunktskarakterer – hvordan vurderer du idræt retfærdigt og dokumenterer det?'
}, {
  title: 'Svømning og udeliv kræver deres egen planlægning',
  text: 'Svømmehal, orienteringsløb, natur og sikkerhed – og vejret vender planerne på hovedet i sidste øjeblik.'
}];
export const afterItems = ['Færdig årsplan for 0.–2., 3.–5. og 6.–9. klasse', '12 færdige idrætslektioner minut for minut', 'Opvarmning, hoveddel, afslutning og materialer til hver lektion', 'Hallkort set oppefra', 'Printklare lektionskort', 'Hver øvelse i tre niveauer', 'Bygget på Fælles Mål for idræt', 'Udeliv, orienteringsløb og svømning', 'Færdighedstest, evalueringsmatrix og 100 færdige feedbackkommentarer'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Aftenerne går med planlægning',
  text: 'Bruger du aftener og weekender på at planlægge næste uges idrætstimer?'
}, {
  icon: TimerIcon,
  title: 'Timen starter, før du er klar',
  text: 'Går du ind i hallen og tænker stadig over, hvad opvarmningen skal være, og hvilke redskaber I skal bruge?'
}, {
  icon: UsersIcon,
  title: 'Store niveauforskelle på holdet',
  text: 'Mangler du øvelser, der virker både for begyndere og for dem, der går til sport flere gange om ugen?'
}, {
  icon: MapIcon,
  title: 'Opstillingen stjæler tid fra timen',
  text: 'Går for meget af lektionen med at stille stationer op, dele hold og forklare regler?'
}, {
  icon: ClipboardCheckIcon,
  title: 'Evalueringen føles uklar',
  text: 'Kunne du bruge færdige færdighedstest, kriterier og feedbackkommentarer, så evaluering og standpunkt bliver retfærdig og velbegrundet?'
}, {
  icon: WavesIcon,
  title: 'Svømning og udeliv giver ekstra arbejde',
  text: 'Savner du færdige forløb til svømning, orienteringsløb og natur og udeliv?'
}];

// Spine colours mirror the physical box (blue, light blue, green, yellow, orange, red).
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Bind 01',
  level: '0.–9. klasse',
  title: 'Årsplan og 12 færdige idrætslektioner',
  tagline: 'Du ved præcis, hvad der skal ske i hver time – hele skoleåret.',
  intro: 'Første bind giver dig færdige årsplaner til alle klassetrin og tolv idrætslektioner, som du kan bruge med det samme.',
  items: ['Årsplaner for 0.–2., 3.–5. og 6.–9. klasse', '40 skoleuger koblet til Fælles Mål', '12 færdige lektioner minut for minut', 'Lektioner på 45 minutter', 'Opvarmning, hoveddel, afslutning og materialer', 'Lærerens instruktion til hver lektion', 'Hallkort set oppefra', 'Printklare lektionskort i tre niveauer', 'Færdighedstest og evalueringsmatrix'],
  outro: 'Færdige lektioner, så du går ind i idrætshallen med en klar plan.',
  spine: 'bg-blue-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Bind 02',
  level: '0.–3. klasse',
  title: 'Leg, kropsbasis og bevægelsesglæde',
  tagline: 'Et stærkt fundament for bevægelse – gennem leg.',
  intro: 'Andet bind bygger kropsbasis op med lege, redskabsbaner og stationer – så selv de mindste oplever at lykkes.',
  items: ['Balance', 'Løb og hop', 'Kast og grib', 'Lege og fangelege', 'Redskabsbaner og stationstræning', 'Dans og rytme', 'Vandtilvænning', 'Øvelser i tre niveauer'],
  outro: 'Aktiv idræt, hvor glæde og læring går hånd i hånd.',
  spine: 'bg-sky-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Bind 03',
  level: '4.–6. klasse',
  title: 'Boldbasis, boldspil og fair play',
  tagline: 'Boldspil, hvor alle kommer med.',
  intro: 'Tredje bind tager eleverne fra boldbasis til regler, roller og fair play – med en tydelig progression.',
  items: ['Fodbold', 'Håndbold', 'Basketball', 'Rundbold', 'Floorball', 'Badminton', 'Regler og roller', 'Fair play og samarbejde', 'Progression fra let til svær'],
  outro: 'Tydelige boldspilstimer, hvor alle elever er med – ikke kun de bedste spillere.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Bind 04',
  level: '0.–9. klasse',
  title: 'Udeliv, orienteringsløb og svømning',
  tagline: 'Idræt uden for hallen – fra skoven til svømmehallen.',
  intro: 'Fjerde bind dækker natur og udeliv, orienteringsløb og svømning – sikkert og færdigplanlagt.',
  items: ['Natur og udeliv', 'Orienteringsløb', 'Svømning', 'Livredning', 'Sikkerhed ude og i vandet', 'Påklædning efter vejret', 'Øvelser i tre niveauer'],
  outro: 'Færdige forløb til de timer, der normalt kræver mest forberedelse.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Bind 05',
  level: '7.–9. klasse',
  title: 'Krop, træning og trivsel',
  tagline: 'Udskolingen lærer at forstå deres egen krop og træning.',
  intro: 'Femte bind gennemgår træningsprincipperne og hjælper eleverne med at lægge deres eget træningsprogram.',
  items: ['Kondition, styrke, hurtighed og bevægelighed', 'Træningsprincipper', 'Eget træningsprogram', 'Søvn og restitution', 'Selvevaluering', 'Øvelser i tre niveauer'],
  outro: 'Færdige lektioner, der gør trivsel til handling i stedet for teori.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Bind 06',
  level: '0.–9. klasse',
  title: 'Evaluering og feedback',
  tagline: 'Retfærdig, velbegrundet og tydelig evaluering i idræt.',
  intro: 'Sjette bind samler evalueringsværktøjerne: tegn på læring, observation, selv- og makkerevaluering og færdige feedbackkommentarer.',
  items: ['Tegn på læring', 'Observationsskemaer', 'Selv- og makkerevaluering', 'Færdighedstest', 'Teoriopgaver A/B med facit', 'Standpunkt i 8.–9. klasse (7-trinsskalaen)', '100 færdige feedbackkommentarer'],
  outro: 'Solid støtte til en retfærdig evaluering – uden aftener med papirarbejde.',
  note: 'Materialet er udarbejdet uafhængigt og er ikke en officiel udgivelse fra Børne- og Undervisningsministeriet.',
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: '6 digitale bind til idræt',
  text: 'Et komplet undervisningssystem, der hjælper dig med at planlægge og gennemføre idrætsundervisningen hele skoleåret. Hvert bind har 40 sider med færdige lektioner, hallkort, lektionskort og øvelser i tre niveauer.'
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Færdige lektioner minut for minut',
  text: 'Slut med det tomme papir. Opvarmning, hoveddel, afslutning, materialer og lærerens instruktion – hver lektion på 45 minutter er klar.'
}, {
  icon: LayoutGridIcon,
  title: 'Hallkort og lektionskort',
  text: 'Du ser med det samme, hvor stationer og redskaber skal stå. Print lektionskortet og tag det med i hallen, eller brug det på tablet.'
}, {
  icon: FileTextIcon,
  title: 'Tre niveauer',
  text: 'Den samme øvelse i tre niveauer, så alle elever – fra begynderen til den sportsvante – kan være med og lykkes.'
}, {
  icon: LayersIcon,
  title: 'Bygget på Fælles Mål',
  text: 'Alsidig idrætsudøvelse, idrætskultur og relationer samt krop, træning og trivsel – med kompetencemålene efter 2., 5., 7. og 9. klassetrin.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Evaluering klar',
  text: 'Færdighedstest, evalueringsmatrix, tegn på læring, standpunkt i 8.–9. klasse og 100 færdige feedbackkommentarer.'
}, {
  icon: CalendarCheckIcon,
  title: 'Årsplan',
  text: '40 skoleuger sat i system – med svømning, udeliv og idrætsdag de rigtige steder i skoleåret.'
}];
// Bind 1 pages (images in data/assets.ts: PAGE_IMAGE_1 … PAGE_IMAGE_5).
export const pageSamples: Array<{
  src: string;
  label: string;
  title: string;
  text: string;
}> = [{
  src: PAGE_IMAGE_1,
  label: 'Bind 1 · Forside',
  title: 'Årsplan + 12 færdige lektioner minut for minut',
  text: 'Bind 1 dækker 0.–2., 3.–5. og 6.–9. klasse og bygger på Fælles Mål for idræt.'
}, {
  src: PAGE_IMAGE_2,
  label: 'Bind 1 · Side 12',
  title: 'Lektion 1 · 0.–2. kl. · 45 min · Dyrene i skoven',
  text: 'Færdig lektion: mål, materialer, forløb minut for minut, hallkort og præcis hvad du siger til eleverne.'
}, {
  src: PAGE_IMAGE_3,
  label: 'Bind 1 · Side 13',
  title: 'Lektionskort · Lektion 1',
  text: 'Hele lektionen på ét kort: tidsplan, hallkort, stikord og tre niveauer.'
}, {
  src: PAGE_IMAGE_4,
  label: 'Bind 1 · Side 36',
  title: 'Færdighedstest · 0.–6. klasse',
  text: 'Otte stationer med tydelige pointanvisninger – print og brug med det samme.'
}, {
  src: PAGE_IMAGE_5,
  label: 'Bind 1 · Side 39',
  title: 'Evalueringsmatrix',
  text: 'Bevægelsesfærdigheder, samarbejde, indsats, fair play og evaluering af egen indsats på fire niveauer – med færdige kommentarer.'
}];
export const pageTypes = ['Færdige lektioner', 'Hallkort', 'Lektionskort', 'Færdighedstest', 'Evaluering'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Vikarpakken – 12 færdige idrætslektioner til vikaren',
  text: 'Tolv færdige lektioner, som vikaren kan gennemføre uden forberedelse – også når hallen ikke er ledig.',
  items: ['12 færdige lektioner på 45 minutter', 'Til 0.–2., 3.–5. og 6.–9. klasse', 'Hallkort og regler', 'Instruktion til vikaren'],
  benefit: 'Ingen spildte timer, når du er væk.'
}, {
  title: '5-minutters bevægelsespauser – 180 aktiviteter til klasseværelset',
  text: 'Korte bevægelsespauser til klasseværelset: aktiverende, rolige, balance- og samarbejdsaktiviteter.',
  items: ['180 aktiviteter', 'Nok til hele skoleåret', '5 minutter ad gangen', 'Varighed, niveau og materialer ved hver aktivitet'],
  benefit: 'Mere bevægelse i skoledagen – uden ekstra planlægning.'
}, {
  title: 'Billedstøtte til idrætshallen – printbare billed- og stationskort',
  text: 'Tydelige billedkort til lektionens forløb, signaler, regler og stationer – store billeder og lidt tekst.',
  items: ['Stationskort i tre niveauer', 'Lektionens forløb og signaler', 'Hallens regler i billeder', 'Selvevalueringskort'],
  benefit: 'Mindre forklaring – mere bevægelse.'
}, {
  title: 'Idrætsdagen trin for trin – skolens idrætsdag og motionsdag',
  text: 'En færdig plan for skolens idrætsdag og motionsdag med tidsplan og poster.',
  items: ['Tidslinje for planlægningen', 'Poster og tidsplaner', 'Idéer til motionsdagen', 'Sikkerhed og information'],
  benefit: 'Idrætsdagen kører – uden stress i sidste øjeblik.'
}, {
  title: 'Skole–hjem-kommunikation – færdige Aula-beskeder og breve til forældre',
  text: 'Færdige beskedskabeloner til forældrene: idrætstøj, svømning, udeliv, ture og feedback.',
  items: ['Færdige Aula-beskeder', 'Breve til forældre', 'Idrætstøj og omklædning', 'Svømning og ture'],
  benefit: 'Tydelig kommunikation med hjemmet – på få sekunder.'
}];
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Åbn IDRÆT KLAR™',
  text: 'Download pakken, og åbn det bind, der passer til dit klassetrin.'
}, {
  title: 'Vælg klasse og lektion',
  text: 'Find den lektion, den aktivitet eller det lektionskort, der passer til dit hold.'
}, {
  title: 'Forbered dig på 5 minutter',
  text: 'Læs lærerens instruktion, se hallkortet, og find redskaberne frem.'
}, {
  title: 'Gå ind i idrætshallen',
  text: 'Print lektionskortet, eller brug det på tablet – og start timen med en klar plan.'
}];
export const audienceItems = ['Du underviser i idræt i 0.–9. klasse', 'Du er klasselærer og har idræt på skemaet', 'Du er idrætslærer og vil spare tid på planlægningen', 'Du underviser i idræt uden at have det som linjefag', 'Du vil have en færdig årsplan bygget på Fælles Mål', 'Du har hold med store niveauforskelle', 'Du vil have hjælp til evaluering og standpunkt', 'Du vil have dine aftener og weekender tilbage'];
export const timeValueTasks = ['Lede efter øvelser og lege på nettet', 'Planlægge lektioner og tegne hallkort', 'Tilpasse øvelser til forskellige niveauer', 'Organisere svømning og udeliv', 'Evaluere og give feedback'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 digitale bind – 240 sider',
  detail: 'Årsplan og færdige lektioner, leg og kropsbasis, boldspil og fair play, udeliv og svømning, krop og træning samt evaluering'
}, {
  label: '5 bonusser uden ekstra betaling',
  detail: 'Vikarpakken, 5-minutters bevægelsespauser, Billedstøtte til idrætshallen, Idrætsdagen trin for trin og Skole–hjem-kommunikation'
}, {
  label: 'Til 0.–9. klasse',
  detail: 'Færdige lektioner, hallkort, lektionskort i tre niveauer og evaluering'
}];
export const offerIncludes = ['Øjeblikkelig digital adgang', 'PDF-filer, du kan gemme', 'Klar til print', 'Der sendes intet fysisk produkt'];
export const PRICE = '199\u00A0DKK';
export const PRICE_BEFORE = '399\u00A0DKK';

// Only add authentic reviews of IDRÆT KLAR™. The section stays hidden while empty.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];
export const faqs = [{
  q: 'Hvordan får jeg materialet?',
  a: 'IDRÆT KLAR™ leveres digitalt. Efter købet får du adgang til PDF-filerne, som du kan downloade og gemme. Du kan bruge materialet på computer, tablet eller mobil og printe de sider, du har brug for.'
}, {
  q: 'Følger materialet Fælles Mål for idræt?',
  a: 'Ja. Årsplaner og lektioner bygger på Fælles Mål for idræt og de tre kompetenceområder alsidig idrætsudøvelse, idrætskultur og relationer samt krop, træning og trivsel – med kompetencemålene efter 2., 5., 7. og 9. klassetrin. IDRÆT KLAR™ er udarbejdet uafhængigt og er ikke en officiel udgivelse fra Børne- og Undervisningsministeriet.'
}, {
  q: 'Er det klar til den nye fagplan?',
  a: 'Den nye fagplan for idræt er stadig et udkast og forventes først at gælde fra 1. august 2027. IDRÆT KLAR™ arbejder allerede med boldbasis og boldspil, dans og udtryk, redskabsaktiviteter samt krop og træning – områder, der også står i udkastet. Lektionerne kan derfor bruges i dag og tilpasses, når den endelige fagplan foreligger.'
}, {
  q: 'Hvilke klassetrin passer materialet til?',
  a: 'Materialet dækker 0.–9. klasse. Bind 2 er til 0.–3. klasse, bind 3 til 4.–6. klasse og bind 5 til 7.–9. klasse. Bind 1, 4 og 6 har materiale til alle klassetrin. Du vælger lektionerne efter dit eget hold.'
}, {
  q: 'Kan jeg virkelig forberede en lektion på 5 minutter?',
  a: 'IDRÆT KLAR™ er lavet til at gøre forberedelsen hurtigere. Du åbner bindet, vælger en færdig lektion, ser hallkortet og læser lærerens instruktion på omkring fem minutter. Tiden kan variere efter lektion, redskaber og hold.'
}, {
  q: 'Rækker materialet til hele skoleåret?',
  a: 'IDRÆT KLAR™ indeholder årsplaner for 40 skoleuger samt færdige lektioner og forløb i alle bind. Du vælger, kombinerer og tilpasser materialet efter din egen plan, dine halltider og dine elever.'
}, {
  q: 'Virker det, hvis der er store niveauforskelle på holdet?',
  a: 'Ja. Øvelserne og lektionskortene findes i tre niveauer, så den samme lektion fungerer både for begyndere og for elever, der dyrker meget sport.'
}, {
  q: 'Er svømning og udeliv med?',
  a: 'Ja. Bind 4 dækker natur og udeliv, orienteringsløb, svømning og livredning – med sikkerhedsanvisninger.'
}, {
  q: 'Er evaluering og standpunkt med?',
  a: 'Ja. Bind 6 indeholder tegn på læring, observationsskemaer, selv- og makkerevaluering, færdighedstest, teoriopgaver A/B med facit, støtte til standpunktskarakterer i 8.–9. klasse efter 7-trinsskalaen og 100 færdige feedbackkommentarer.'
}, {
  q: 'Skal jeg printe det hele?',
  a: 'Nej. Du vælger selv, hvilke sider du printer. Lektionskortene fungerer også fra en tablet i idrætshallen.'
}, {
  q: 'Er de fem bonusser med i prisen?',
  a: `Ja. Alle fem bonusser er med i pakken uden ekstra betaling – det hele for ${PRICE}.`
}, {
  q: 'Er det et abonnement?',
  a: 'Nej. Du betaler én gang og får adgang til det digitale materiale. Ingen løbende betalinger.'
}, {
  q: 'Kan jeg bruge materialet på mobil eller tablet?',
  a: 'Ja. PDF-filerne kan åbnes på computer, tablet og mobil. Til hallkortene anbefaler vi en tablet eller et printet lektionskort.'
}, {
  q: 'Er der garanti?',
  a: 'Ja. IDRÆT KLAR™ har 7 dages tilfredshedsgaranti. Hvis materialet ikke lever op til dine forventninger, kan du kontakte os og få pengene tilbage inden for garantiperioden i henhold til betingelserne.'
}];
export const footerLinks = ['Handelsbetingelser', 'Privatlivspolitik', 'Returnering', 'Kontakt'];
